import { getIn } from 'formik';

import type { ProductsByPlantFormikType } from '../formik/quote';
import type { SharedFormikType, ProductSectionType } from '../hooks/useProductSection';
import {
  NewCurrency,
  type Currency,
  type CurrencyWireFormat,
} from '../types/api/Currency/Currency';
import Enums from '../types/api/Enums';
import type { FixedPricingByCompany } from '../types/api/FixedPricingByCompany/FixedPricingByCompany';
import { hasStringValue, hasValue } from './equality';

export enum AppliedTierType {
  None = 'None',
  Fixed = 'Fixed',
  Imported = 'Imported',
  Adjustment = 'Adjustment',
}

export type TierPriceFloor = {
  basePrice: Currency;
  adjustment: Currency;
};

export type TierAdjustment = [
  tierPrice: Currency | null | undefined,
  appliedTierType: AppliedTierType,
  floor?: TierPriceFloor,
];

function adjustedAtOrAboveZero(basePrice: Currency, adjustment: Currency): TierAdjustment {
  const adjusted = basePrice.add(adjustment);
  if (!hasValue(adjusted) || basePrice.toUnit() < 0 || adjusted.toUnit() >= 0) {
    return [adjusted, AppliedTierType.Adjustment];
  }
  return [
    NewCurrency({ number: '0', currency: basePrice.currency }),
    AppliedTierType.Adjustment,
    { basePrice, adjustment },
  ];
}

export function calculateTierAdjustment(
  fixedPricingByCompany: FixedPricingByCompany | undefined,
  originalPrice: Currency | undefined,
  locationId: string | null | undefined,
  productId: string | null | undefined,
): TierAdjustment {
  if (!(fixedPricingByCompany && locationId && productId)) {
    return [originalPrice, AppliedTierType.None];
  }

  const location = fixedPricingByCompany[locationId];
  if (!location) {
    return [originalPrice, AppliedTierType.None];
  }

  const product = location.products[productId];
  if (product) {
    return [NewCurrency({ number: product.amount, currency: 'USD' }), AppliedTierType.Fixed];
  }

  const importedProduct = location.importedProducts?.[productId];
  if (importedProduct) {
    return [
      NewCurrency({ number: importedProduct.amount, currency: 'USD' }),
      AppliedTierType.Imported,
    ];
  }

  if (location.adjustment) {
    if (!originalPrice?.number) {
      return [undefined, AppliedTierType.None];
    }

    if (location.adjustment.type === Enums.MarginConfiguration.FlatDollar) {
      return adjustedAtOrAboveZero(
        originalPrice,
        NewCurrency({
          number: location.adjustment.value,
          currency: originalPrice.currency,
        }),
      );
    }
    const multiplied = originalPrice.multiply(0.01 * parseFloat(location.adjustment.value));
    if (!multiplied) {
      return [undefined, AppliedTierType.None];
    }
    return adjustedAtOrAboveZero(originalPrice, multiplied);
  }

  return [originalPrice, AppliedTierType.None];
}

export function priceSourceLabel(
  appliedTierType: AppliedTierType,
  isAtTierPrice: boolean,
  isAtBasePrice = false,
): string {
  if (!isAtTierPrice) {
    return appliedTierType === AppliedTierType.None && isAtBasePrice ? 'Base' : 'Manual';
  }

  switch (appliedTierType) {
    case AppliedTierType.Fixed:
      return 'Fixed';
    case AppliedTierType.Imported:
      return 'Imported';
    case AppliedTierType.Adjustment:
      return 'Tier';
    default:
      return 'Manual';
  }
}

function formatDecrease(adjustment: Currency, currencySymbol: string): string {
  return `-${currencySymbol}${Math.abs(adjustment.toUnit()).toFixed(2)}`;
}

export function tierPriceFloorLabel(floor: TierPriceFloor, currencySymbol: string): string {
  return `Tier pricing set to ${currencySymbol}0.00. The adjustment (${formatDecrease(floor.adjustment, currencySymbol)}) would drop the price below zero.`;
}

export function tierPriceFloorBreakdown(
  floor: TierPriceFloor,
  currencySymbol: string,
): { label: string; amount: string }[] {
  return [
    { label: 'Base', amount: `${currencySymbol}${floor.basePrice.toUnit().toFixed(2)}` },
    { label: 'Adjustment', amount: formatDecrease(floor.adjustment, currencySymbol) },
    { label: 'Tier price', amount: `${currencySymbol}0.00` },
  ];
}

export function tierPriceStatusLabel({
  appliedTierType,
  isAtTierPrice,
  netUnitPrice,
  tierNetPrice,
  hasDiscounts,
  currencySymbol,
  floor,
}: {
  appliedTierType: AppliedTierType;
  isAtTierPrice: boolean;
  netUnitPrice: number;
  tierNetPrice: number;
  hasDiscounts: boolean;
  currencySymbol: string;
  floor?: TierPriceFloor;
}): string {
  const source = priceSourceLabel(appliedTierType, true);
  if (isAtTierPrice) {
    return hasValue(floor)
      ? tierPriceFloorLabel(floor, currencySymbol)
      : `${source} pricing applied`;
  }
  if (!hasDiscounts) {
    return `Price differs from ${source} pricing`;
  }

  const flooredTierNet = Math.max(0, tierNetPrice);
  const difference = netUnitPrice - flooredTierNet;
  const tierNet = `${source} pricing less discounts (${currencySymbol}${flooredTierNet.toFixed(2)})`;
  if (Math.abs(difference) < 0.005) {
    return `Matches ${tierNet}`;
  }
  const direction = difference < 0 ? 'below' : 'above';
  return `${currencySymbol}${Math.abs(difference).toFixed(2)} ${direction} ${tierNet}`;
}

export function getPricingChipLabel(
  hasTierPricing: boolean,
  fixedPricingByCompany: FixedPricingByCompany | undefined,
  allProductsByPlants: ProductsByPlantFormikType[],
): string | null {
  if (!hasTierPricing || !hasValue(fixedPricingByCompany)) {
    return null;
  }

  let hasFixed = false;
  let hasImported = false;
  let hasAdjusted = false;

  for (const plantGroup of allProductsByPlants) {
    if (hasFixed && hasImported && hasAdjusted) {
      break;
    }

    const plantId = plantGroup.plant?.id;
    if (hasStringValue(plantId)) {
      const location = fixedPricingByCompany[plantId];
      if (location) {
        for (const quoteProduct of plantGroup.products) {
          const productId = quoteProduct.product?.id;
          if (hasStringValue(productId)) {
            if (hasValue(location.products[productId])) {
              hasFixed = true;
            } else if (hasValue(location.importedProducts?.[productId])) {
              hasImported = true;
            } else if (hasValue(location.adjustment)) {
              hasAdjusted = true;
            }
          }
        }
      }
    }
  }

  const pricing: string[] = [];
  if (hasFixed) {
    pricing.push('Fixed');
  }
  if (hasImported) {
    pricing.push('Imported');
  }
  if (hasAdjusted) {
    pricing.push('Tier');
  }

  if (pricing.length === 0) {
    return null;
  }

  return `${pricing.join(' & ')} pricing applied`;
}

export function calculateTierAdjustmentFormik(
  formikValues: SharedFormikType,
  formikSectionType: ProductSectionType,
  index: number,
  fixedPricingByCompany: FixedPricingByCompany | undefined,
  locationId: string | null | undefined,
  productId: string | null | undefined,
): TierAdjustment {
  const originalPrice = getIn(formikValues, `${formikSectionType}.${index}.originalPrice`) as
    | CurrencyWireFormat
    | null
    | undefined;
  const basePrice = originalPrice?.number
    ? NewCurrency({
        number: originalPrice.number,
        currency: originalPrice.currency ?? 'USD',
      })
    : undefined;

  return calculateTierAdjustment(fixedPricingByCompany, basePrice, locationId, productId);
}
